import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import RoleManagement from "../components/permissions/RoleManagement";
import {
  ArrowLeft,
  Shield,
  Settings,
  FileText,
  Users,
  LayoutDashboard,
  LogOut,
} from "lucide-react";

export default function RoleManagementPage() {
  const navigate = useNavigate();
  const { user, logout, companyBranding } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <div 
                className="flex items-center gap-2 cursor-pointer" 
                onClick={() => navigate("/")}
              >
                {companyBranding?.logoUrl ? (
                  <img 
                    src={companyBranding.logoUrl} 
                    alt="Logo" 
                    className="h-8 w-auto"
                  />
                ) : (
                  <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="font-heading font-semibold text-primary-700">
                  {companyBranding?.companyName || "MyMeetingsPal"}
                </span>
              </div>
              
              <nav className="hidden md:flex items-center gap-1">
                <Button
                  variant="ghost"
                  className="text-stone-600"
                  onClick={() => navigate("/")}
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  className="text-stone-600"
                  onClick={() => navigate("/settings")}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
                <Button
                  variant="ghost"
                  className="text-primary-600 bg-primary-50"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Role Management
                </Button>
              </nav>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-stone-800">{user?.name}</p>
                <p className="text-xs text-stone-500 capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-stone-500 hover:text-stone-700"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-stone-500 mb-6">
          <button onClick={() => navigate("/")} className="hover:text-stone-700">
            Dashboard
          </button>
          <span>/</span>
          <button onClick={() => navigate("/settings")} className="hover:text-stone-700">
            Settings
          </button>
          <span>/</span>
          <span className="text-stone-700 font-medium">Role Management</span>
        </div>

        {/* Role Management Component */}
        <RoleManagement />

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            About Governance Roles
          </h3>
          <p className="text-sm text-blue-700 mb-4">
            Governance roles control what actions users can perform on meetings and minutes. 
            Each role has specific permissions designed for their responsibilities.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Role Scopes</h4>
              <ul className="text-blue-700 space-y-1">
                <li>• <strong>Global:</strong> Applies to all meetings</li>
                <li>• <strong>Committee:</strong> Applies to specific committee meetings</li>
                <li>• <strong>Meeting:</strong> Applies to a single meeting only</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 mb-1">Role Priority</h4>
              <ul className="text-blue-700 space-y-1">
                <li>• Meeting-specific roles take precedence</li>
                <li>• Then committee-specific roles</li>
                <li>• Finally global roles</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
